import type {
  DataProvider,
  CrudFilters,
  CrudSorting,
  LogicalFilter,
} from "@refinedev/core";
import { ADMIN_API_URL } from "@/lib/constants";
import { FIXTURES } from "./fixtures";

/**
 * mockDataProvider — implementa a interface DataProvider do Refine resolvendo
 * tudo contra os fixtures em memória (src/mock/fixtures.ts), com a MESMA
 * semântica do simple-rest que a API real usa:
 *
 *   - getList: aplica filtros → ordena (sorters / `_sort`/`_order`) → pagina
 *     (pagination ou `_start`/`_end`) e devolve { data, total } (total = X-Total-Count).
 *   - getOne:  busca por id.
 *   - update:  faz merge no registro em memória (toggle de feature-flag, editar user…).
 *   - create / deleteOne: mutam o array (moderação remove entry/brain).
 *   - getMany: vários ids.
 *   - custom:  parse de query string com _start/_end/_sort/_order para quem
 *     chamar rotas agregadas; cai no mesmo motor de getList.
 *
 * Como cada resource obtém dados: o `resource` (name do Refine) indexa
 * FIXTURES[resource]. Não há rede — nenhuma tela quebra sem API.
 */

// store mutável (cópia rasa por resource para podermos update/create/delete)
const store: Record<string, Array<Record<string, unknown>>> = Object.fromEntries(
  Object.entries(FIXTURES).map(([k, v]) => [k, v.map((r) => ({ ...r }))]),
);

function rowsFor(resource: string): Array<Record<string, unknown>> {
  return store[resource] ?? [];
}

// ── filtros (suporta os operadores comuns do Refine) ────────────────────────────
function applyFilters(
  rows: Array<Record<string, unknown>>,
  filters?: CrudFilters,
): Array<Record<string, unknown>> {
  if (!filters?.length) return rows;
  return rows.filter((row) =>
    filters.every((f) => {
      // conjunções "and"/"or" → trata recursivamente (raro no admin; suporte básico)
      if ((f as { operator?: string }).operator === "or") {
        const sub = (f as { value: CrudFilters }).value;
        return applyFilters([row], sub).length > 0;
      }
      const lf = f as LogicalFilter;
      const field = lf.field;
      const val = row[field];
      const target = lf.value;
      switch (lf.operator) {
        case "eq":
          return String(val) === String(target);
        case "ne":
          return String(val) !== String(target);
        case "lt":
          return Number(val) < Number(target);
        case "lte":
          return Number(val) <= Number(target);
        case "gt":
          return Number(val) > Number(target);
        case "gte":
          return Number(val) >= Number(target);
        case "contains":
          return String(val ?? "").toLowerCase().includes(String(target).toLowerCase());
        case "in":
          return Array.isArray(target) && target.map(String).includes(String(val));
        case "null":
          return val === null || val === undefined;
        case "nnull":
          return val !== null && val !== undefined;
        default:
          return String(val ?? "").toLowerCase().includes(String(target ?? "").toLowerCase());
      }
    }),
  );
}

// ── ordenação ────────────────────────────────────────────────────────────────────
function applySort(
  rows: Array<Record<string, unknown>>,
  sorters?: CrudSorting,
): Array<Record<string, unknown>> {
  if (!sorters?.length) return rows;
  const list = [...rows];
  list.sort((a, b) => {
    for (const s of sorters) {
      const av = a[s.field];
      const bv = b[s.field];
      let cmp = 0;
      if (av == null && bv != null) cmp = -1;
      else if (av != null && bv == null) cmp = 1;
      else if (typeof av === "number" && typeof bv === "number") cmp = av - bv;
      else cmp = String(av).localeCompare(String(bv));
      if (cmp !== 0) return s.order === "desc" ? -cmp : cmp;
    }
    return 0;
  });
  return list;
}

const delay = <T>(value: T): Promise<T> =>
  new Promise((resolve) => setTimeout(() => resolve(value), 120));

export const mockDataProvider: DataProvider = {
  getApiUrl: () => ADMIN_API_URL,

  getList: async ({ resource, pagination, filters, sorters }) => {
    let rows = rowsFor(resource);
    rows = applyFilters(rows, filters);
    rows = applySort(rows, sorters);
    const total = rows.length;

    const mode = pagination?.mode ?? "server";
    if (mode !== "off") {
      const current = pagination?.current ?? 1;
      const pageSize = pagination?.pageSize ?? 10;
      const start = (current - 1) * pageSize;
      rows = rows.slice(start, start + pageSize);
    }
    return delay({ data: rows as never, total });
  },

  getOne: async ({ resource, id }) => {
    const row = rowsFor(resource).find((r) => String(r.id) === String(id));
    return delay({ data: (row ?? {}) as never });
  },

  getMany: async ({ resource, ids }) => {
    const set = new Set(ids.map(String));
    const rows = rowsFor(resource).filter((r) => set.has(String(r.id)));
    return delay({ data: rows as never });
  },

  create: async ({ resource, variables }) => {
    const arr = rowsFor(resource);
    const record = {
      id: `${resource}_${Date.now()}`,
      ...(variables as Record<string, unknown>),
    };
    arr.unshift(record);
    return delay({ data: record as never });
  },

  update: async ({ resource, id, variables }) => {
    const arr = rowsFor(resource);
    const idx = arr.findIndex((r) => String(r.id) === String(id));
    if (idx >= 0) {
      arr[idx] = { ...arr[idx], ...(variables as Record<string, unknown>) };
      // bumpa updated_at quando o campo existe (feature-flags)
      if ("updated_at" in arr[idx]) arr[idx].updated_at = new Date().toISOString();
      return delay({ data: arr[idx] as never });
    }
    return delay({ data: { id, ...(variables as Record<string, unknown>) } as never });
  },

  deleteOne: async ({ resource, id }) => {
    const arr = rowsFor(resource);
    const idx = arr.findIndex((r) => String(r.id) === String(id));
    let removed: Record<string, unknown> = { id };
    if (idx >= 0) removed = arr.splice(idx, 1)[0];
    return delay({ data: removed as never });
  },

  /**
   * custom — para rotas agregadas (ai-cost, billing…). Resolve o segmento final
   * da URL como nome de resource e aplica _start/_end/_sort/_order da query.
   */
  custom: async ({ url, query }) => {
    let resource = url.split("?")[0].split("/").filter(Boolean).pop() ?? "";
    if (!store[resource]) resource = "ai-metrics";
    let rows = rowsFor(resource);

    const q = (query ?? {}) as Record<string, unknown>;
    if (q._sort) {
      const fields = String(q._sort).split(",");
      const orders = String(q._order ?? "asc").split(",");
      rows = applySort(
        rows,
        fields.map((field, i) => ({
          field,
          order: (orders[i] ?? orders[0]) === "desc" ? "desc" : "asc",
        })),
      );
    }
    const total = rows.length;
    if (q._start != null || q._end != null) {
      const start = Number(q._start ?? 0);
      const end = Number(q._end ?? rows.length);
      rows = rows.slice(start, end);
    }
    return delay({ data: { data: rows, total } as never });
  },
};
